import { ChevronDown, ChevronUp } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useState } from "react";

export default function FAQ() {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqs = [
    {
      question: "How quickly can I hire a developer?",
      answer: "You can hire pre-vetted South African developers within 7 days. Our streamlined process includes requirement gathering, developer matching, and interviews - all completed in record time."
    },
    {
      question: "What's included in your service?",
      answer: "We provide pre-vetted developers, 7-day guarantee, risk-free trial period, ongoing support, and flexible contracts. No recruitment fees or long-term commitments required."
    },
    {
      question: "What technologies do your developers specialize in?",
      answer: "Our developers excel in JavaScript frameworks, React Native, iOS, Flutter, TypeScript, Python, Ruby, PHP, and FinTech solutions. We also have data scientists and UI/UX designers available."
    },
    {
      question: "How do you vet your developers?",
      answer: "All developers undergo rigorous technical interviews, portfolio reviews, and soft skills assessments. We verify their experience, English proficiency, and cultural fit for global teams."
    },
    {
      question: "What are your pricing and payment terms?",
      answer: "You only pay when you hire. No upfront costs, subscription fees, or recruitment charges. Flexible contracts with competitive hourly rates based on developer expertise and project requirements."
    },
    {
      question: "Do developers work in my timezone?",
      answer: "South African developers are in the same timezone as Europe and offer 6-hour overlap with US East Coast. This provides excellent coverage for meetings and real-time collaboration."
    },

    {
      question: "How does project communication work?",
      answer: "Developers use standard tools like Slack, Jira, and Trello. Most projects include daily standups or regular sync meetings to ensure clear communication and project alignment."
    },
    {
      question: "Can I hire for both short-term and long-term projects?",
      answer: "We require a 6-month minimum engagement because we want to build long-term relationships with our clients. This benefits both you and the contractor as they learn your business, understand your processes, and become more effective over time."
    },
    {
      question: "What support do you provide after hiring?",
      answer: "We provide ongoing support throughout the engagement, including dedicated account management, performance monitoring, and assistance with any issues that may arise."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark-slate to-charcoal text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyber-purple rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-drift"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-cyber-blue rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-orbit" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-neon-pink rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-spiral" style={{animationDelay: '4s'}}></div>
      </div>

      <Navigation onBookCall={() => {}} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              Frequently Asked <span className="text-cyber-purple text-shadow">Questions</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Everything you need to know about hiring pre-vetted South African developers
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-8">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="glass-card rounded-2xl overflow-hidden">
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-800/30 transition-colors"
                  >
                    <h3 className="text-xl font-semibold text-white pr-4">{faq.question}</h3>
                    {openItems.includes(index) ? (
                      <ChevronUp className="text-cyber-purple flex-shrink-0" size={24} />
                    ) : (
                      <ChevronDown className="text-cyber-purple flex-shrink-0" size={24} />
                    )}
                  </button>
                  {openItems.includes(index) && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyber-purple/20 to-neon-pink/20">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Still Have <span className="text-cyber-gold">Questions</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Book a free discovery call and let us help you find the perfect developer for your team.
            </p>
            <a href="/project-form">
              <button className="bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 animate-glow">
                Book Free Discovery Call
              </button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}